/**
 * Confirmation modal for the header's "Mark as On Hold" / "Mark as Lost"
 * actions. Each action records its own required reason, which lands in the
 * account history alongside the status change. The account leaves the active
 * pipeline but stays reachable through the On Hold / Lost status filters on
 * the Accounts list.
 */

import React from 'react';
import {Button} from '@common-web/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';

/** The two off-pipeline statuses. Lost is stored as REJECTED. */
export type MarkStatusTarget = 'ON_HOLD' | 'REJECTED';

const STATUS_COPY: Record<
  MarkStatusTarget,
  {title: string; description: string; confirm: string; confirming: string; placeholder: string}
> = {
  ON_HOLD: {
    title: 'Mark as On Hold',
    description:
      'will be paused and removed from the active pipeline. It stays available under the On Hold status filter and can be resumed later.',
    confirm: 'Mark as On Hold',
    confirming: 'Marking…',
    placeholder: 'Why is this account being put on hold?',
  },
  REJECTED: {
    title: 'Mark as Lost',
    description:
      'will be marked lost and removed from the active pipeline. It stays available under the Lost status filter and can be reopened later.',
    confirm: 'Mark as Lost',
    confirming: 'Marking…',
    placeholder: 'Why was this account lost?',
  },
};

interface MarkStatusModalProps {
  /** Which status is being applied; null keeps the dialog closed. */
  target: MarkStatusTarget | null;
  companyName: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  confirming?: boolean;
}

export function MarkStatusModal({
  target,
  companyName,
  onConfirm,
  onCancel,
  confirming = false,
}: MarkStatusModalProps): React.ReactElement {
  const [reason, setReason] = React.useState('');

  // Fresh form each time the dialog opens — a stale reason from a previous
  // status change must never be submitted silently.
  React.useEffect(() => {
    if (target) setReason('');
  }, [target]);

  const copy = target ? STATUS_COPY[target] : null;
  const trimmedReason = reason.trim();

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy?.title}</DialogTitle>
          <DialogDescription>
            <strong>{companyName}</strong> {copy?.description}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-1.5 py-2'>
          <Label htmlFor='mark-status-reason'>Reason</Label>
          <Textarea
            id='mark-status-reason'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={copy?.placeholder}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onCancel} disabled={confirming}>
            Cancel
          </Button>
          <Button
            variant='destructive'
            disabled={trimmedReason === '' || confirming}
            onClick={() => onConfirm(trimmedReason)}
          >
            {confirming ? copy?.confirming : copy?.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
